import {useErrorHandler} from '@/hooks/errorHandler';
import ApiConnector from '@/backend/auth/auth.js'
import { isImpersonationTab } from '@/backend/auth/tokenStorage.mjs'
import { createToaster } from "@meforma/vue-toaster";

const api = new ApiConnector()
const ROLES_CACHE_KEY = 'erp:roles:v1'
const PERMISSIONS_CATALOG_CACHE_KEY = 'erp:permissions-catalog:v1'
const ME_CACHE_KEY = 'erp:me:v2'
const ROLES_CACHE_TTL_MS = 60 * 60 * 1000
const PERMISSIONS_CATALOG_TTL_MS = 60 * 60 * 1000
const ME_CACHE_TTL_MS = 5 * 60 * 1000
let rolesRequest = null
let permissionsCatalogRequest = null
let meRequest = null

function readCache(key) {
  try {
    const cached = JSON.parse(localStorage.getItem(key))
    if (!cached?.data || !cached?.savedAt) return null
    return cached
  } catch {
    return null
  }
}

function writeCache(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify({
      savedAt: Date.now(),
      data,
    }))
  } catch {
    // ignore storage quota/private mode errors
  }
}

function removeCache(key) {
  try {
    localStorage.removeItem(key)
  } catch {
    // ignore
  }
}

// Имперсонализация живёт в sessionStorage (per-tab): токен целевого пользователя
// доставляется через postMessage из окна админа (см. main.js handoff).
const isImpersonating = isImpersonationTab

function impersonationInfo() {
  try {
    return JSON.parse(sessionStorage.getItem('impersonation')) || null
  } catch {
    return null
  }
}

function clearUserSWRCache() {
  removeCache(ROLES_CACHE_KEY)
  removeCache(ME_CACHE_KEY)
}

// /users/me может вернуть не объект (HTML SPA-шелл при ошибке прокси/бэкенда,
// строку ошибки и т.п.) — такие данные нельзя ни коммитить, ни кэшировать.
function isValidUser(payload) {
  return !!payload && typeof payload === 'object' && !Array.isArray(payload)
}

// Нормализуем роль в объект: API иногда отдаёт role_id без вложенного role,
// или роль строкой. Гарантируем role.name для шаблона и геттеров.
function normalizeUser(payload) {
  const role = (payload.role && typeof payload.role === 'object')
    ? payload.role
    : { name: typeof payload.role === 'string' && payload.role ? payload.role : 'Гость' }
  return { ...payload, role }
}

export default {
  

  // role:{id: 1}, authIsReady: true - тестовые значения, админка
  state: () => ({
    user: {role_id:99,role:{name:'Гость'},name:'Гость'},
    authIsReady: false,
    users:[],
    roles_title:null,
    permissions_catalog:null,
    statusLoading: false
  }),
  
  mutations: {
    deleteUser(state, id) {
      const index = state.users.findIndex(user => user.id === id);
      if (index !== -1) {
        state.users.splice(index, 1);
      }
    },
    setLoader(state, status) {
      state.statusLoading = status;
    },

    setUser(state,payload){
      let user = {role_id:99,role:{name:'Гость'},name:'Гость'}
      if(isValidUser(payload)){
        user = normalizeUser(payload)
      }
      state.user = user
    },
    setRoles(state,payload){
      state.roles_title = payload
    },

    setPermissionsCatalog(state,payload){
      state.permissions_catalog = payload
    },

    setUsers(state,payload){
      let users = []
      if(payload) users = payload
      state.users = users
    },

    upsertUser(state, payload) {
      if (!payload) return
      const index = state.users.findIndex(user => user.id === payload.id)
      if (index === -1) {
        state.users.unshift(payload)
      } else {
        state.users.splice(index, 1, payload)
      }
      if (state.user?.id === payload.id) {
        state.user = normalizeUser(payload)
      }
    },

    setAuthIsReady(state,payload){
      state.authIsReady = payload
    }

  },

  getters: {
    isAdmin(state){
      const allowed_role = ['superadmin','Админ','Руководитель','Руководитель отдела']
      if (allowed_role.includes(state.user.role.name)  ){
        return true
      }
      return false
    },
    isManager(state){
      return state.user.role.name === "Гость" || state.user.role.name === "Менеджер";
    },
    isSupervisor(state){
      return state.user.role.name !== "Гость" && state.user.role.name !== "Менеджер";
    },
    hasPermission: (state) => (permission) => {
      const role = state.user.role || {}
      const permissions = role.permissions || []
      if (permission === 'distribution.approvals' && role.name === 'Админ') return true
      return role.name === 'superadmin' || permissions.includes('ALL') || permissions.includes(permission)
    },
    isImpersonating() {
      return isImpersonating()
    },
    impersonationName() {
      return impersonationInfo()?.name || ''
    },
    isTripApprovalEnabled(state) {
      return state.user?.features?.trip_approval_enabled === true
    },
  },

  actions: {
    async deleteUser(ctx,userId){
      try{
        const response = await api.deleteUser(userId);
        if(response.status === 200){
          ctx.commit('deleteUser',userId)
          return response.data
        }
      } catch (error) {
        return useErrorHandler(error);
      }
    },

    async updateRole(ctx, { roleId, data }){
      try{
        const response = await api.updateRole(roleId, data);
        if(response.status === 200){
          removeCache(ROLES_CACHE_KEY)
          ctx.dispatch('getUsers')
          return response.data
        }
      } catch (error) {
        return useErrorHandler(error);
      }
    },

    async logout(ctx){
      // При имперсонализации — выходим только из сессии целевого пользователя
      // (sessionStorage), НЕ трогая токен админа в localStorage, и закрываем вкладку.
      if (isImpersonating()) {
        sessionStorage.removeItem('user')
        sessionStorage.removeItem('impersonation')
        window.close()
        return
      }
      await api.logout();
      clearUserSWRCache()
      ctx.commit('setUser',null)
      ctx.commit('setRoles',null)
    },

    async login(ctx, { email, password }) {
      try {
        const response = await api.login(email, password);
        if (response.status === 200) {
          clearUserSWRCache()
          await ctx.dispatch('getUser');
        }
        return null
      } catch (error) {
        return useErrorHandler(error);
      }
    },

    async register(ctx, { email, password,name }) {
      
      const toaster = createToaster();
      try {

        const response = await api.register(email, password,name);
        if (response.status === 200) {
          ctx.dispatch('getUsers')
          toaster.success('Пользователь добавлен')
        }
        return response.data
      } catch (error) {
        return useErrorHandler(error);
      }
    },

    async getUsers(ctx) {      
      ctx.commit('setLoader', true);
      try {
        const response = await api.getUsersV2();
        if (response.status === 200){
            await ctx.commit('setUsers', response.data);
        }
      } catch (error) {
        return useErrorHandler(error);
      } finally {
        ctx.commit('setLoader', false);
      }
    },

    async getRoles(ctx) {
      // В вкладке имперсонализации НЕ используем общий localStorage-кэш ролей
      // (он принадлежит сессии админа) — только свежие данные под токеном цели.
      const impersonating = isImpersonating()
      const cached = impersonating ? null : readCache(ROLES_CACHE_KEY)
      if (cached?.data) {
        await ctx.commit('setRoles', cached.data)
      }

      const isFresh = cached && Date.now() - cached.savedAt < ROLES_CACHE_TTL_MS
      if (isFresh) return null
      if (rolesRequest) return cached?.data ? null : rolesRequest

      rolesRequest = (async () => {
        try {
          const response = await api.getRoles();
          if (response.status === 200){
              await ctx.commit('setRoles', response.data);
              if (!impersonating) writeCache(ROLES_CACHE_KEY, response.data)
          }
          return null
        } catch (error) {
          if (!cached?.data) return useErrorHandler(error);
          return null
        } finally {
          rolesRequest = null
        }
      })()

      if (cached?.data) return null
      return rolesRequest
    },

    async getPermissionsCatalog(ctx) {
      const cached = readCache(PERMISSIONS_CATALOG_CACHE_KEY)
      if (cached?.data) {
        await ctx.commit('setPermissionsCatalog', cached.data)
      }

      const isFresh = cached && Date.now() - cached.savedAt < PERMISSIONS_CATALOG_TTL_MS
      if (isFresh) return null
      if (permissionsCatalogRequest) return cached?.data ? null : permissionsCatalogRequest

      permissionsCatalogRequest = (async () => {
        try {
          const response = await api.getPermissionsCatalog();
          if (response.status === 200 && Array.isArray(response.data)) {
            await ctx.commit('setPermissionsCatalog', response.data);
            writeCache(PERMISSIONS_CATALOG_CACHE_KEY, response.data)
          }
          return null
        } catch (error) {
          if (!cached?.data) return useErrorHandler(error);
          return null
        } finally {
          permissionsCatalogRequest = null
        }
      })()

      if (cached?.data) return null
      return permissionsCatalogRequest
    },

    async getUser(ctx) {
      const impersonating = isImpersonating()
      let path = null
      const cached = impersonating ? null : readCache(ME_CACHE_KEY)

      if (cached?.data && isValidUser(cached.data)) {
        await ctx.commit('setUser', cached.data)
        if (!ctx.state.authIsReady) {
          await ctx.commit('setAuthIsReady', true)
        }
      } else if (cached?.data && !isValidUser(cached.data)) {
        // кэш отравлен (например, HTML вместо объекта) — чистим
        removeCache(ME_CACHE_KEY)
      }

      const isFresh = cached && Date.now() - cached.savedAt < ME_CACHE_TTL_MS
      if (isFresh) return null
      if (meRequest) return cached?.data ? null : meRequest

      meRequest = (async () => {
        try{
          const response = await api.getUser();
          if(response.status === 200 && isValidUser(response.data)){
            await ctx.commit('setUser', response.data);
            if (!impersonating) writeCache(ME_CACHE_KEY, response.data)
          }
        }catch(error){
          if (!cached?.data) {
            path = useErrorHandler(error);
          }
        }finally{
          if (!ctx.state.authIsReady) {
            await ctx.commit('setAuthIsReady', true);
          }
          meRequest = null
        }
        return path
      })()

      if (cached?.data) return null
      return meRequest
    },

    async updateUser({commit},form){
      const { response, request } = api.put('/users/',form)
      try{
        await request()
        if(response.value){
          commit('setUser', response.value);
          // Не пишем в общий localStorage-кэш из вкладки имперсонализации —
          // иначе закисаем сессию админа данными целевого пользователя.
          if (!isImpersonating()) writeCache(ME_CACHE_KEY, response.value)
          // toaster.success('Задача на обновление добавлена')
        }
      }catch(err){
        // toaster.error('Не удалось добавить задачу')
      }
    },

    async createUserAdmin(ctx, form) {
      try {
        const response = await api.createUserV2(form);
        if (response.status === 200) {
          ctx.commit('upsertUser', response.data.user);
        }
        return response.data;
      } catch (error) {
        return useErrorHandler(error);
      }
    },

    async updateUserAdmin(ctx, { userId, form }) {
      try {
        const response = await api.updateUserV2(userId, form);
        if (response.status === 200) {
          ctx.commit('upsertUser', response.data);
          if (ctx.state.user?.id === response.data?.id) {
            writeCache(ME_CACHE_KEY, response.data)
          }
        }
        return response.data;
      } catch (error) {
        return useErrorHandler(error);
      }
    },

    async setUserActiveAdmin(ctx, { userId, isActive }) {
      try {
        const response = await api.setUserActiveV2(userId, isActive);
        if (response.status === 200) {
          ctx.commit('upsertUser', response.data);
          if (ctx.state.user?.id === response.data?.id) {
            writeCache(ME_CACHE_KEY, response.data)
          }
        }
        return response.data;
      } catch (error) {
        return useErrorHandler(error);
      }
    },

    async resetUserPasswordAdmin(ctx, userId) {
      try {
        const response = await api.resetUserPasswordV2(userId);
        return response.data;
      } catch (error) {
        return useErrorHandler(error);
      }
    },

    async endImpersonation(ctx) {
      sessionStorage.removeItem('user')
      sessionStorage.removeItem('impersonation')
      ctx.commit('setUser', null)
      ctx.commit('setAuthIsReady', false)
      try { window.close() } catch { /* ignore */ }
    },

    async impersonateUser(ctx, userId) {
      try {
        const response = await api.impersonate(userId);
        return response.data;
      } catch (error) {
        return useErrorHandler(error);
      }
    },

  }

};

